import { useState } from 'react'
import { Link } from 'wouter'
import { BackdropOrbs } from '../components/BackdropOrbs'
import { SiteFooter } from '../components/SiteFooter'
import { CharacterForm, emptyFormValues, type CharacterFormValues } from '../components/CharacterForm'
import { ApiError } from '../lib/api'
import { createCharacter, saveManageKey } from '../lib/characters'

/** Create a community character: title, tagline, optional image link, up to
 *  three links, a description, and a lorebook entry capped at ~500 tokens. */

type Created = { id: string; title: string; manageKey: string }

export function CharacterNew() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<Created | null>(null)

  const submit = async (values: CharacterFormValues) => {
    setSubmitting(true)
    try {
      const res = await createCharacter(values)
      saveManageKey(res.character.id, res.manageKey)
      setCreated({ id: res.character.id, title: res.character.title, manageKey: res.manageKey })
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong while filing the character. Try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (created) {
    return (
      <div className="lorebook-root flex min-h-screen flex-col">
        <BackdropOrbs />
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col items-center justify-center px-4 py-16">
          <div className="w-full rounded-lg border border-signal/40 bg-void-2/80 p-8 text-center backdrop-blur">
            <div className="text-[11px] uppercase tracking-[0.35em] text-signal/70">Filed</div>
            <h1 className="mt-2 font-display text-3xl font-semibold text-white">
              {created.title} enters the system
            </h1>
            <p className="mt-3 text-sm text-slate-400">
              Your character is live on the board. Save this manage key if you ever want to edit or
              delete it. It is shown only once.
            </p>
            <div className="mt-5 select-all break-all rounded-lg border border-edge bg-void px-4 py-3 font-mono text-xs text-signal">
              {created.manageKey}
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(created.manageKey)}
                className="rounded-full border border-edge px-4 py-1.5 text-sm text-slate-300 transition hover:border-signal/60 hover:text-white"
              >
                Copy key
              </button>
              <Link
                href={`/characters/${created.id}`}
                className="rounded-full border border-signal/50 bg-signal/10 px-4 py-1.5 text-sm text-signal transition hover:bg-signal/20 hover:text-white"
              >
                View character
              </Link>
              <Link
                href="/characters"
                className="rounded-full border border-edge px-4 py-1.5 text-sm text-slate-300 transition hover:border-signal/60 hover:text-white"
              >
                Back to gallery
              </Link>
            </div>
          </div>
        </div>
        <SiteFooter />
      </div>
    )
  }

  return (
    <div className="lorebook-root flex min-h-screen flex-col">
      <BackdropOrbs />
      <header className="sticky top-0 z-30 border-b border-edge bg-void/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 lg:px-6">
          <div className="flex items-center gap-3">
            <Link
              href="/characters"
              className="text-sm text-slate-300 transition hover:text-signal"
            >
              ← Characters
            </Link>
            <span className="text-slate-700">/</span>
            <span className="font-display text-sm text-white">New</span>
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-2xl flex-1 px-4 py-10 lg:px-6">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.35em] text-signal/70">New character</div>
          <h1 className="mt-2 font-display text-4xl font-semibold text-white">File a character</h1>
          <p className="mt-2 text-sm text-slate-400">
            Only the title and tagline are required. Everything else can stay empty and fill in
            later, the card just shows WIP.
          </p>
        </div>

        <CharacterForm
          initial={emptyFormValues}
          submitting={submitting}
          serverError={error}
          submitLabel="File character"
          onSubmit={submit}
        />
      </div>
      <SiteFooter />
    </div>
  )
}
